/**
 * Akış bots. Difficulty = win-rate band of a noisy greedy bot (a stand-in for an engaged human),
 * plus a deterministic lookahead bot as the "this level is solvable" witness.
 *
 *  - greedy (noise 0): scores every launch by blocks cleared, shooters emptied and tray pressure,
 *    looks `depth` launches ahead, never takes a launch that loses on the spot.
 *  - noisy (noise ε): with probability ε taps any legal launch instead of the best (can lose).
 *  - random: any legal launch (a distracted player; used for information only).
 */
import { Rng } from "@enjoyment/core";
import { applyAction, cloneState, createGame, evalLaunch } from "./sim";
import type { Action, Difficulty, Level, State } from "./types";
import type { Picture } from "@enjoyment/core";

export interface BotOpts {
  /** Probability of a non-best choice per decision. */
  noise: number;
  /** Launches looked ahead after the candidate (0 = pure one-ply greedy). */
  depth: 0 | 1 | 2;
  seed: number;
}

export const GREEDY: BotOpts = { noise: 0, depth: 2, seed: 1 };

const LOSS = -1e6;

function candidates(s: State, scratch: Int8Array): Action[] {
  const out: Action[] = [];
  for (let i = 0; i < s.queues.length; i++) if (s.queues[i]!.length && evalLaunch(s, "queue", i, scratch).legal) out.push({ t: "q", i });
  for (let i = 0; i < s.tray.length; i++) if (evalLaunch(s, "tray", i, scratch).legal) out.push({ t: "t", i });
  return out;
}

function scoreAction(s: State, a: Action, depth: number, scratch: Int8Array): number {
  const t = cloneState(s);
  const r = applyAction(t, a, { checkStuck: false, detail: false, record: false });
  if (!r.ok) return LOSS;
  if (t.status === "won") return 1e6;
  if (t.status === "lost") return LOSS;
  let v = r.shotCount * 10 + (r.outcome === "emptied" ? 4 : 0) - t.tray.length * 3 - t.locks * 3;
  if (r.shotCount === 0) v -= 6; // banking a shooter is a last resort
  if (depth > 0) {
    const next = candidates(t, scratch);
    if (next.length === 0) return LOSS / 2;
    let best = -Infinity;
    for (const n of next) {
      const x = scoreAction(t, n, depth - 1, scratch);
      if (x > best) best = x;
    }
    v += 0.6 * best;
  }
  return v;
}

/** Pick the next launch for the bot, or null when no legal launch exists. */
export function chooseAction(s: State, rng: Rng, opts: BotOpts = GREEDY): Action | null {
  if (s.status !== "playing") return null;
  const scratch = new Int8Array(s.cover.length);
  const cands = candidates(s, scratch);
  if (cands.length === 0) return null;
  // A distracted tap: any legal launch, fatal ones included (this is what makes the grade informative).
  if (opts.noise > 0 && cands.length > 1 && rng.next() < opts.noise) return cands[rng.int(cands.length)]!;
  const scored = cands.map((a) => ({ a, v: scoreAction(s, a, opts.depth, scratch) + rng.next() * 0.001 }));
  scored.sort((x, y) => y.v - x.v);
  return scored[0]!.a;
}

export interface PlayResult {
  won: boolean;
  launches: number;
  state: State;
}

export function playBot(level: Level, picture: Picture | null, opts: BotOpts = GREEDY): PlayResult {
  const s = createGame(level, picture);
  const rng = new Rng(opts.seed);
  for (let guard = 0; s.status === "playing" && guard < 800; guard++) {
    const a = chooseAction(s, rng, opts);
    if (!a) {
      s.status = "lost";
      s.lostBy = "stuck";
      break;
    }
    applyAction(s, a);
  }
  return { won: s.status === "won", launches: s.launches, state: s };
}

/** Uniformly random legal launches, for the "distracted player" column of the grade. */
export function playRandom(level: Level, seed: number): boolean {
  const s = createGame(level);
  const rng = new Rng(seed);
  const scratch = new Int8Array(s.cover.length);
  for (let guard = 0; s.status === "playing" && guard < 800; guard++) {
    const c = candidates(s, scratch);
    if (c.length === 0) return false;
    applyAction(s, c[rng.int(c.length)]!, { checkStuck: false, detail: false, record: false });
  }
  return s.status === "won";
}

export interface Grade {
  /** Win rate of the noisy greedy bot over `samples` seeds: the difficulty signal. */
  winRate: number;
  /** Deterministic lookahead bot wins (the level is solvable). */
  greedyWins: boolean;
  randomWinRate: number;
  difficulty: Difficulty | "broken";
  samples: number;
}

export const NOISY: Omit<BotOpts, "seed"> = { noise: 0.25, depth: 1 };

export function bandOf(winRate: number, greedyWins: boolean): Difficulty | "broken" {
  if (!greedyWins || winRate < 0.3) return "broken";
  if (winRate >= 0.95) return "easy";
  if (winRate >= 0.8) return "medium";
  if (winRate >= 0.5) return "hard";
  return "expert";
}

/** README bands: easy >=0.95, medium >=0.8, hard >=0.5, expert [0.3,0.5), <0.3 (or unsolvable) broken. */
export function grade(level: Level, samples = 12, randomSamples = 0): Grade {
  const greedyWins = playBot(level, null, GREEDY).won;
  let wins = 0;
  if (greedyWins) for (let i = 0; i < samples; i++) if (playBot(level, null, { ...NOISY, seed: 1000 + i }).won) wins++;
  const winRate = greedyWins ? wins / samples : 0;
  let rw = 0;
  for (let i = 0; i < randomSamples; i++) if (playRandom(level, 5000 + i)) rw++;
  return { winRate, greedyWins, randomWinRate: randomSamples ? rw / randomSamples : 0, difficulty: bandOf(winRate, greedyWins), samples };
}
